import Card from "@/components/card"
import PagePlaceholder from "@/components/page-placeholder"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import mapStore from "@/entities/map/model/map.store"
import {
  getOverviewAreaCenter,
  getOverviewCoverMetrics,
  projectOverviewPoint,
  projectPointInOverviewArea
} from "@/entities/map/model/map-overview.utils"
import MapChip from "@/entities/map/ui/MapChip"
import siteStore from "@/entities/site/model/site.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import PhysicalRiskCard from "@/entities/physical-risk/ui/PhysicalRiskCard"
import getPolygonCentroid from "@/utils/get-polygon-centroid.utils"
import { getLineMidpoint, getPrimaryGeometryPath } from "@/utils/site-geometry.utils"
import env from "@/env"
import {
  faExpand,
  faMinus,
  faPencilAlt,
  faPlus,
  faTriangleExclamation,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useMemo, useRef, useState, useEffect } from "react"
import { useMediaQuery } from "react-responsive"
import { useNavigate } from "react-router-dom"
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import establishmentStore from "../model/establishment.store"
import { useRisksSpace } from "@/core/risks-space.context"
import DashboardCardTitle, { DASHBOARD_MAX_ITEMS } from "./DashboardCardTitle"
import useOpenRiskCatalog from "@/entities/physical-risk/hooks/useOpenRiskCatalog"

const LABEL_SLOTS = [
  { position: { left: 16, top: 16 } },
  { position: { right: 16, top: 16 } },
  { position: { right: 16, bottom: 16 } }
]
const MAX_RISK_LABEL_LENGTH = 32
//Espace entre deux cartes de risque (gap-10), compte dans la place qu'elles prennent
const RISK_CARD_GAP = 10

type TransformState = { scale: number; positionX: number; positionY: number }

function getSlotLineStart(slot: (typeof LABEL_SLOTS)[number], containerSize: { width: number; height: number }) {
  return {
    x: "left" in slot.position ? 112 : containerSize.width - 112,
    y: "top" in slot.position ? 34 : containerSize.height - 34
  }
}

function segmentsCross(
  firstStart: { x: number; y: number },
  firstEnd: { x: number; y: number },
  secondStart: { x: number; y: number },
  secondEnd: { x: number; y: number }
) {
  function orientation(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }) {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
  }

  return (
    orientation(firstStart, firstEnd, secondStart) * orientation(firstStart, firstEnd, secondEnd) < 0 &&
    orientation(secondStart, secondEnd, firstStart) * orientation(secondStart, secondEnd, firstEnd) < 0
  )
}

function getSlotPermutations<T>(slots: T[]): T[][] {
  if (slots.length <= 1) return [slots]
  return slots.flatMap((slot, index) =>
    getSlotPermutations(slots.filter((_, slotIndex) => slotIndex !== index)).map((remaining) => [slot, ...remaining])
  )
}

function truncateRiskLabel(label: string) {
  if (label.length <= MAX_RISK_LABEL_LENGTH) return label
  return `${label.slice(0, MAX_RISK_LABEL_LENGTH - 1).trimEnd()}…`
}

interface Props {
  onAddClick?: () => void
  onEditClick?: () => void
}

export default function EstablishmentOverviewCard({ onAddClick, onEditClick }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { getPath } = useRisksSpace()
  const openRiskCatalog = useOpenRiskCatalog()
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const maps = useAppSelector(mapStore.selectors.list)
  const sites = useAppSelector(siteStore.selectors.list)
  const physicalRisks = useAppSelector(physicalRiskStore.selectors.list)
  const risksContainerRef = useRef<HTMLDivElement | null>(null)
  const [fittingRiskCount, setFittingRiskCount] = useState(0)
  const isSmall = useMediaQuery({ query: "(max-width: 1439px)" })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapPointerStartRef = useRef<{ x: number; y: number; mapId: string } | null>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [imageVersion] = useState(() => Date.now())
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null)
  const [hoveredMapId, setHoveredMapId] = useState<string | null>(null)
  const [transform, setTransform] = useState<TransformState>({ scale: 1, positionX: 0, positionY: 0 })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const updateSize = () => setContainerSize({ width: container.offsetWidth, height: container.offsetHeight })
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  //Sans plan importe, aucun lieu a situer : la carte montre les risques physiques les plus eleves
  const topRisks = useMemo(
    () =>
      [...physicalRisks]
        .sort((a, b) => (b.cValueEffective ?? -1) - (a.cValueEffective ?? -1))
        .slice(0, DASHBOARD_MAX_ITEMS),
    [physicalRisks]
  )

  //Seules les cartes entieres restent visibles, comme les mesures de la carte Prevention. Les autres sortent
  //du flux sans disparaitre : leur hauteur reste mesurable, et le flex centre celles qui tiennent.
  useEffect(() => {
    const container = risksContainerRef.current
    if (!container) return
    const updateCount = () => {
      let usedHeight = -RISK_CARD_GAP
      let count = 0
      for (const card of Array.from(container.children) as HTMLElement[]) {
        usedHeight += RISK_CARD_GAP + card.offsetHeight
        if (usedHeight > container.clientHeight) break
        count++
      }
      setFittingRiskCount(count)
    }
    updateCount()
    const observer = new ResizeObserver(updateCount)
    observer.observe(container)
    return () => observer.disconnect()
  }, [maps.length, topRisks])

  const coverMetrics = useMemo(() => getOverviewCoverMetrics(containerSize, imageSize), [containerSize, imageSize])

  const riskSites = useMemo(() => {
    return sites
      .flatMap((site) => {
        if (!site.MapId || !site.cRiskMax) return []
        const map = maps.find((item) => item.id === site.MapId)
        if (!map?.overviewArea) return []
        const pointOnMap =
          site.geometryType === "line"
            ? getLineMidpoint(site.geometry)
            : getPolygonCentroid(getPrimaryGeometryPath("polygon", site.geometry))
        return [{ site, map, risk: site.cRiskMax, point: projectPointInOverviewArea(pointOnMap, map.overviewArea) }]
      })
      .sort((a, b) => (b.risk.cValueEffective ?? 0) - (a.risk.cValueEffective ?? 0))
  }, [maps, sites])

  const displayedSites = useMemo(() => {
    if (!coverMetrics) return []
    const visibleSites = riskSites
      .map((item) => {
        const basePoint = projectOverviewPoint(item.point, coverMetrics)
        return {
          ...item,
          screenPoint: {
            x: basePoint.x * transform.scale + transform.positionX,
            y: basePoint.y * transform.scale + transform.positionY
          }
        }
      })
      .filter(
        (item) =>
          item.screenPoint.x >= 0 &&
          item.screenPoint.x <= containerSize.width &&
          item.screenPoint.y >= 0 &&
          item.screenPoint.y <= containerSize.height
      )

    const moderateSites = visibleSites.filter((item) => item.risk.cValueEffectiveIndex >= 1)
    //Un slot d'etiquette par site : au-dela, l'affectation n'aurait plus de place ou se poser.
    const maxSites = Math.min(isSmall ? 2 : 3, LABEL_SLOTS.length)
    return (moderateSites.length ? moderateSites : visibleSites.slice(0, 1)).slice(0, maxSites)
  }, [containerSize, coverMetrics, isSmall, riskSites, transform])

  const positionedSites = useMemo(() => {
    const slotPermutations = getSlotPermutations(LABEL_SLOTS).map((slots) => slots.slice(0, displayedSites.length))
    const bestSlots = slotPermutations.reduce(
      (best, slots) => {
        const segments = displayedSites.map((item, index) => ({
          start: getSlotLineStart(slots[index], containerSize),
          end: item.screenPoint
        }))
        let crossings = 0
        for (let firstIndex = 0; firstIndex < segments.length; firstIndex++) {
          for (let secondIndex = firstIndex + 1; secondIndex < segments.length; secondIndex++) {
            if (
              segmentsCross(
                segments[firstIndex].start,
                segments[firstIndex].end,
                segments[secondIndex].start,
                segments[secondIndex].end
              )
            ) {
              crossings++
            }
          }
        }
        const length = segments.reduce(
          (total, segment) => total + Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y),
          0
        )
        const score = crossings * 1_000_000 + length
        return score < best.score ? { score, slots } : best
      },
      { score: Number.POSITIVE_INFINITY, slots: slotPermutations[0] ?? [] }
    ).slots

    return displayedSites.map((item, index) => ({ ...item, slot: bestSlots[index] }))
  }, [containerSize, displayedSites])

  const hoveredMapLabel = useMemo(() => {
    if (!coverMetrics || !hoveredMapId) return null
    const map = maps.find((item) => item.id === hoveredMapId)
    if (!map?.overviewArea) return null
    const center = projectOverviewPoint(getOverviewAreaCenter(map.overviewArea), coverMetrics)
    return {
      name: map.name,
      x: center.x * transform.scale + transform.positionX,
      y: center.y * transform.scale + transform.positionY
    }
  }, [coverMetrics, hoveredMapId, maps, transform])

  const imageSrc = `${env.REACT_APP_URL_SPACE}/Establishments/${establishment.id}.png?v=${imageVersion}`

  function openSite(siteId: string, mapId: string, riskId: string) {
    const site = sites.find((item) => item.id === siteId)
    const map = maps.find((item) => item.id === mapId)
    if (!site || !map) return
    dispatch(mapStore.actions.setActive(map))
    dispatch(siteStore.actions.setActive(site))
    navigate(getPath(`/sites/${site.id}?riskId=${riskId}`))
  }

  function openMap(mapId: string) {
    const map = maps.find((item) => item.id === mapId)
    if (!map) return
    dispatch(mapStore.actions.setActive(map))
    dispatch(siteStore.actions.setActive(null))
    navigate(getPath("/sites"))
  }

  function ControlButton(icon: IconDefinition, onClick: () => void, title: string) {
    return (
      <button
        type="button"
        title={title}
        onClick={(event) => {
          event.stopPropagation()
          onClick()
        }}
        className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm _hover">
        <FontAwesomeIcon icon={icon} />
      </button>
    )
  }

  function openRisks() {
    openRiskCatalog()
  }

  //La carte mene aux risques physiques ; la vue (zoom, deplacement, lieux) garde ses clics pour elle.
  const cardClassName =
    "_hover mb-0 flex min-w-0 flex-1 flex-col overflow-hidden [&>.card-title]:px-[30px] [&>.card-title]:pt-[20px] [&>.page-loader]:min-h-0 [&>.page-loader]:flex-1 [&>.page-loader>div]:flex [&>.page-loader>div]:min-h-0 [&>.page-loader>div]:min-w-0 [&>.page-loader>div]:flex-1"
  const cardTitle = <DashboardCardTitle icon={faTriangleExclamation} title="Risques physiques" onDetailClick={openRisks} />

  if (!maps.length) {
    return (
      <Card isWithoutPadding className={cardClassName} title={cardTitle} onClick={openRisks}>
        <div
          ref={risksContainerRef}
          //Marge basse : le pendant de celle du titre, comme le padding de la carte Prevention
          className="relative mb-[20px] flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-10 overflow-hidden px-[30px]"
          onClick={(event) => event.stopPropagation()}>
          {topRisks.map((risk, index) => (
            //Espacement porte par le gap, comme la carte Prevention : la marge haute de la carte est retiree
            <div
              key={risk.id}
              className={`[&>*]:!mt-0 ${index < fittingRiskCount ? "" : "invisible absolute left-[30px] right-[30px] top-0"}`}>
              <PhysicalRiskCard risk={risk} isWithoutMeasures />
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (!establishment.siteImageUploaded) {
    return (
      <Card isWithoutPadding className={cardClassName} title={cardTitle} onClick={openRisks}>
        <div className="flex flex-1 items-center justify-center" onClick={(event) => event.stopPropagation()}>
          <PagePlaceholder
            title="Aucune vue d'ensemble disponible"
            help="Visualisez les lieux de travail présentant les risques les plus élevés dans votre établissement."
            image="start"
            button={onAddClick ? "Ajouter une image" : undefined}
            onClick={onAddClick}
          />
        </div>
      </Card>
    )
  }

  return (
    <Card isWithoutPadding className={cardClassName} title={cardTitle} onClick={openRisks}>
      <div
        ref={containerRef}
        className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
        onClick={(event) => event.stopPropagation()}>
        <TransformWrapper
          initialScale={1}
          minScale={1}
          maxScale={6}
          centerOnInit
          wheel={{ step: 0.12 }}
          pinch={{ step: 5 }}
          doubleClick={{ mode: "zoomIn" }}
          panning={{ velocityDisabled: true }}
          onTransformed={(_, state) =>
            setTransform({ scale: state.scale, positionX: state.positionX, positionY: state.positionY })
          }>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{ width: "100%", height: "100%" }}>
                <div className="relative h-full w-full">
                  <img
                    src={imageSrc}
                    alt="Vue d'ensemble de l'établissement"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.85]"
                    onLoad={(event) =>
                      setImageSize({
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight
                      })
                    }
                  />

                  {coverMetrics && (
                    <svg className="absolute inset-0 z-[1] h-full w-full">
                      {maps.map((map) => {
                        if (!map.overviewArea) return null
                        const points = [
                          map.overviewArea.p1,
                          map.overviewArea.p2,
                          map.overviewArea.p3,
                          map.overviewArea.p4
                        ].map((point) => projectOverviewPoint(point, coverMetrics))
                        const isHovered = hoveredMapId === map.id
                        return (
                          <g key={map.id}>
                            <polygon
                              points={points.map((point) => `${point.x},${point.y}`).join(" ")}
                              fill="white"
                              fillOpacity={isHovered ? 0.28 : 0}
                              stroke="white"
                              strokeOpacity={isHovered ? 0.8 : 0}
                              strokeWidth="2"
                              className="cursor-pointer"
                              style={{ pointerEvents: "all" }}
                              onMouseEnter={() => setHoveredMapId(map.id)}
                              onMouseLeave={() => setHoveredMapId(null)}
                              onPointerDown={(event) => {
                                mapPointerStartRef.current = { x: event.clientX, y: event.clientY, mapId: map.id }
                              }}
                              onClick={(event) => {
                                event.stopPropagation()
                                const start = mapPointerStartRef.current
                                mapPointerStartRef.current = null
                                if (
                                  !start ||
                                  start.mapId !== map.id ||
                                  Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4
                                )
                                  return
                                openMap(map.id)
                              }}
                            />
                          </g>
                        )
                      })}
                    </svg>
                  )}
                </div>
              </TransformComponent>

              {coverMetrics &&
                positionedSites.map(({ site, map, risk, screenPoint: dot, slot }) => {
                  const labelPosition = slot.position
                  const lineStart = getSlotLineStart(slot, containerSize)
                  const riskColor = risk.cValueEffectiveColor ?? "#94a3b8"
                  const isHovered = hoveredSiteId === site.id

                  const riskLabel = risk.cName || risk.cFamily?.name || "Risque"

                  function openRisk(event: React.MouseEvent) {
                    event.stopPropagation()
                    openSite(site.id, map.id, risk.id)
                  }

                  return (
                    <div key={site.id}>
                      <svg className="pointer-events-none absolute inset-0 z-[2] h-full w-full">
                        <line
                          x1={lineStart.x}
                          y1={lineStart.y}
                          x2={dot.x}
                          y2={dot.y}
                          stroke={riskColor}
                          strokeWidth={isHovered ? 6 : 4}
                          strokeLinecap="round"
                          opacity={isHovered ? 1 : 0.9}
                        />
                        <line
                          x1={lineStart.x}
                          y1={lineStart.y}
                          x2={dot.x}
                          y2={dot.y}
                          stroke="transparent"
                          strokeWidth="18"
                          strokeLinecap="round"
                          className="cursor-pointer"
                          style={{ pointerEvents: "stroke" }}
                          onMouseEnter={() => setHoveredSiteId(site.id)}
                          onMouseLeave={() => setHoveredSiteId(null)}
                          onClick={openRisk}
                        />
                        <circle cx={dot.x} cy={dot.y} r={isHovered ? 9 : 7} fill={riskColor} />
                        <circle
                          cx={dot.x}
                          cy={dot.y}
                          r="14"
                          fill="transparent"
                          className="cursor-pointer"
                          style={{ pointerEvents: "all" }}
                          onMouseEnter={() => setHoveredSiteId(site.id)}
                          onMouseLeave={() => setHoveredSiteId(null)}
                          onClick={openRisk}
                        />
                      </svg>
                      <button
                        type="button"
                        className="absolute z-[3] border-0 bg-transparent p-0 shadow-md _hover"
                        style={labelPosition}
                        onMouseEnter={() => setHoveredSiteId(site.id)}
                        onMouseLeave={() => setHoveredSiteId(null)}
                        onClick={openRisk}
                        title={riskLabel}>
                        <MapChip color={riskColor} name={truncateRiskLabel(riskLabel)} />
                      </button>
                    </div>
                  )
                })}

              {hoveredMapLabel && (
                <div
                  className="pointer-events-none absolute z-[5] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-white px-3 py-1 text-sm font-bold text-slate-700 shadow-sm"
                  style={{ left: hoveredMapLabel.x, top: hoveredMapLabel.y }}>
                  {hoveredMapLabel.name}
                </div>
              )}

              <div className="absolute bottom-[16px] left-[16px] z-[4] flex flex-col gap-2">
                {ControlButton(faPlus, zoomIn, "Zoom avant")}
                {ControlButton(faMinus, zoomOut, "Zoom arrière")}
                {ControlButton(faExpand, resetTransform, "Retour à la vue initiale")}
                {onEditClick && (
                  <button
                    type="button"
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-[12px] border-0 bg-[#EAF2FB] text-[#0E5AA7] shadow-sm _hover"
                    title="Modifier la vue d'ensemble"
                    onClick={(event) => {
                      event.stopPropagation()
                      onEditClick()
                    }}>
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                )}
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
    </Card>
  )
}
